import {isScheduleFilled, isTimeInSchedule} from "@/core/util/date/schedule";
import {BenefitTypeEnum, OrderAttributesValue, OrderCondition} from "@/gql/gen";
import {BasketItemModel} from "@/modules/shop/composable/util";

/**
 * Интерфейс для контекста проверки ограничений заказа
 */
export interface OrderRestrictionContext {
   // benefitType: BenefitTypeEnum | undefined | null;
    /** Атрибуты заказа */
    attrs: OrderAttributesValue;
    /** Расписание ограничения */
    schedule?: any;
    /** Текущий офис заказа */
    office?: {
        ID: number;
        PROPERTIES?: {
            ROLES?: Array<{ CODE: string }>;
        };
    } | null;
    /** Статистика корзины */
    basketStat: {
        productIds: number[];
        sectionIds: number[];
        products: Record<number, { NAME: string }>;
        sections: Record<number, { NAME: string }>;
    };
    /** Элементы корзины */
    basketItems: BasketItemModel[];
    /** Сумма заказа без скидок (корзина + доставка) */
    orderPriceSource?: number;
    /** Сумма заказа со скидками (корзина + доставка) - используется для проверки ORDER_PRICE_FROM */
    orderPriceTotal?: number;
    /** ID выбранного приза из колеса фортуны */
    prizeId?: number | null;
    /** ID активного промокода */
    couponId?: number | null;
}

/**
 * Причина неприменимости ограничения/модификатора
 */
export type RestrictionFailureReason =
    | 'order-price'      // Не хватает суммы заказа (ORDER_PRICE_FROM)
    | 'payment-type'     // Не подходит способ оплаты
    | 'transport-type'   // Не подходит способ доставки
    | 'office'           // Не подходит офис
    | 'office-role'      // Не подходит роль офиса
    | 'product'          // Нет нужных товаров в корзине
    | 'product-section'  // Нет товаров из нужных секций
    | 'schedule'         // Не подходит по расписанию
    | 'prize'            // Не подходит приз из колеса фортуны
    | 'benefit-type'     // Не подходит тип benefit (скидка/бонусы/промокод/приз)
    | 'coupon'           // Не подходит промокод
    | null;              // Ограничение применимо

/**
 * Результат проверки ограничения
 */
export interface RestrictionCheckResult {
    /** Ограничение применимо к текущему заказу */
    isApplicable: boolean;
    /** Причина неприменимости (если isApplicable = false) */
    failureReason: RestrictionFailureReason;
    /** Переменные для шаблонизации сообщений */
    vars: Record<string, string>;
    /** ID товаров, попавших под ограничение */
    matchedProductIds: number[];
    /** ID категорий, попавших под ограничение */
    matchedSectionIds: number[];
}

/**
 * Проверяет, применимо ли ограничение к текущему заказу
 *
 * @param condition - Условия ограничения заказа
 * @param context - Контекст заказа (атрибуты, корзина, офис, расписание)
 * @returns Результат проверки с информацией о применимости, причине неприменимости и совпадениях
 */
export function checkOrderRestriction(
    condition: OrderCondition,
    context: OrderRestrictionContext
): RestrictionCheckResult {
    const result: RestrictionCheckResult = {
        isApplicable: false,
        failureReason: null,
        vars: {},
        matchedProductIds: [],
        matchedSectionIds: []
    };

    const {attrs, schedule, office, basketStat} = context;

    // Проверяем, заданы ли вообще какие-либо условия
    const hasAnyCondition =
        condition.PAYMENT_TYPE.length > 0 ||
        condition.TRANSPORT_TYPE.length > 0 ||
        condition.PRODUCT_SECTION.length > 0 ||
        condition.PRODUCT.length > 0 ||
        condition.OFFICE_ROLE.length > 0 ||
        condition.OFFICE.length > 0 ||
        (condition.BENEFIT_TYPE && condition.BENEFIT_TYPE.length > 0) ||
        (condition.ORDER_PRICE_FROM !== null && condition.ORDER_PRICE_FROM !== undefined) ||
        (condition.PRIZE !== null && condition.PRIZE !== undefined) ||
        (condition.COUPON !== null && condition.COUPON !== undefined);

    // Если никакие условия не заданы, ограничение применимо
    if (!hasAnyCondition) {
        result.isApplicable = true;
        result.failureReason = null;
        return result;
    }


    // Проверка типа benefit
    if (condition.BENEFIT_TYPE && condition.BENEFIT_TYPE.length > 0) {
        if (!attrs.BENEFIT_TYPE || !condition.BENEFIT_TYPE.includes(attrs.BENEFIT_TYPE as any)) {
            result.failureReason = 'benefit-type';
            return result;
        }
    }

    // Проверка типа доставки
    if (condition.TRANSPORT_TYPE.length > 0) {
        if (!attrs.TRANSPORT_TYPE || !condition.TRANSPORT_TYPE.includes(attrs.TRANSPORT_TYPE as any)) {
            result.failureReason = 'transport-type';
            return result;
        }
    }

    // Проверка ролей оффисов
    if (condition.OFFICE_ROLE.length > 0) {
        if (!office) {
            result.failureReason = 'office-role';
            return result;
        }
        const officeRolesArray = (office.PROPERTIES?.ROLES || []).map(r => r.CODE);
        const hasMatchingRole = condition.OFFICE_ROLE.some(role =>
            officeRolesArray.includes(role as string)
        );
        if (!hasMatchingRole) {
            result.failureReason = 'office-role';
            return result;
        }
    }

    // Проверка оффисов
    if (condition.OFFICE.length > 0) {
        if (!office || !condition.OFFICE.includes(office.ID)) {
            result.failureReason = 'office';
            return result;
        }
    }

    // Проверка способа оплаты
    if (condition.PAYMENT_TYPE.length > 0) {
        if (!attrs.PAYMENT_TYPE || !condition.PAYMENT_TYPE.includes(attrs.PAYMENT_TYPE as any)) {
            result.failureReason = 'payment-type';
            return result;
        }
    }

    if (condition.PRIZE) {
        if (!context.prizeId || context.prizeId !== condition.PRIZE) {
            result.failureReason = 'prize';
            return result;
        }
    }

    if (condition.COUPON) {
        if (!context.couponId || context.couponId !== condition.COUPON) {
            result.failureReason = 'coupon';
            return result;
        }
    }

    // Проверка минимальной суммы заказа (ПОСЛЕ скидок)
    // ВАЖНО: ORDER_PRICE_FROM сравнивается с суммой ПОСЛЕ скидок (orderPriceTotal)
    if (condition.ORDER_PRICE_FROM !== null && condition.ORDER_PRICE_FROM !== undefined) {
        const orderPrice = context.orderPriceTotal ?? context.orderPriceSource ?? 0;
        if (orderPrice < condition.ORDER_PRICE_FROM) {
            result.failureReason = 'order-price';
            return result;
        }
    }

    // Проверка расписания
    if (isScheduleFilled(schedule)) {
        if (!isTimeInSchedule(schedule as Record<string, string[]>)) {
            result.failureReason = 'schedule';
            return result;
        }
    }

    // Проверка товаров и категорий
    const productIds = condition.PRODUCT;
    const productSectionIds = condition.PRODUCT_SECTION.map(id => Number(id));

    if (productIds.length > 0 || productSectionIds.length > 0) {
        // Поиск совпадающих товаров
        if (productIds.length > 0) {
            result.matchedProductIds = basketStat.productIds.filter(id =>
                productIds.includes(id)
            );
        }

        // Поиск совпадающих категорий
        if (productSectionIds.length > 0) {
            result.matchedSectionIds = basketStat.sectionIds.filter(id =>
                productSectionIds.includes(id)
            );
        }

        // Если нет совпадений, ограничение не применимо
        if (result.matchedProductIds.length === 0 && result.matchedSectionIds.length === 0) {
            // Определяем причину: товары или секции
            if (productIds.length > 0 && productSectionIds.length === 0) {
                result.failureReason = 'product';
            } else if (productSectionIds.length > 0 && productIds.length === 0) {
                result.failureReason = 'product-section';
            } else {
                // Если заданы и товары, и секции, но ничего не совпало
                result.failureReason = 'product';
            }
            return result;
        }

        // Формирование переменных для шаблонизации
        const productNames: string[] = [];
        const sectionNames: string[] = [];

        result.matchedProductIds.forEach(productId => {
            const product = basketStat.products[productId];
            if (product) {
                productNames.push(`«${product.NAME}»`);
            }
        });

        result.matchedSectionIds.forEach(sectionId => {
            const section = basketStat.sections[sectionId];
            if (section) {
                sectionNames.push(`«${section.NAME}»`);
            }
        });

        result.vars['SECTIONS'] = sectionNames.join(', ');
        result.vars['PRODUCTS'] = productNames.join(', ');

        const parts: string[] = [];
        if (productNames.length > 0) {
            parts.push(productNames.join(', '));
        } else if (sectionNames.length > 0) {
            const prefix = sectionNames.length > 1 ? 'категорий ' : 'категории ';
            parts.push(prefix + sectionNames.join(', '));
        }

        result.vars['PRODUCTS_OR_SECTIONS'] = parts.join(' и ');
    }

    // Ограничение применимо
    result.isApplicable = true;
    result.failureReason = null;
    return result;
}

/**
 * Проверяет, попадает ли конкретный товар корзины под ограничение
 *
 * @param basketItem - Элемент корзины
 * @param matchedProductIds - ID товаров, попавших под ограничение
 * @param matchedSectionIds - ID категорий, попавших под ограничение
 * @returns true, если товар попадает под ограничение
 */
export function isBasketItemRestricted(
    basketItem: BasketItemModel,
    matchedProductIds: number[],
    matchedSectionIds: number[]
): boolean {
    if (!basketItem.isRoot.value) {
        return false;
    }

    const product = basketItem.product;
    if (!product) {
        return false;
    }

    // Проверка по категориям
    const productParentSectionIds = product.IBLOCK_SECTION_IDS || [];
    const hasMatchedSection = matchedSectionIds.some(id =>
        productParentSectionIds.includes(id)
    );

    if (hasMatchedSection) {
        return true;
    }

    // Проверка по ID товара
    if (matchedProductIds.includes(product.ID)) {
        return true;
    }

    return false;
}
